import { useEffect, useState } from "react";
import { Bishop } from "../../interfaces/bishop"; // Bishop interface
import { useNavigate } from "react-router-dom";
import { getStates } from "../../services/helpers";
import { createBishop } from "../../services/bishopService";
import { Denomination } from "../../interfaces/denomination";
import { getDenominations } from "../../services/denominationService";
import ToolTip from "../../components/component-tools/ToolTip";

const CreateBishop = () => {
  const navigate = useNavigate();
  const states = getStates();
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [bishop, setBishop] = useState<Bishop>({
    bishopID: 0,
    diocese: "",
    denomination: "",
    bishopName: "",
    contact: "",
    greeting: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    verified: false,
    comments: "",
    sendCertified: true,
    email: "",
    phone: "",
  });
  const [denominations, setDenominations] = useState<Denomination[]>([]);

  useEffect(() => {
    const fetchDenominations = async () => {
      try {
        setLoadingSpinner(true);

        const denominationsResponse = await getDenominations();
        const excludedDenominations = [2, 3, 4];
        const filteredDenominations = denominationsResponse.filter(
          (denomination) =>
            !excludedDenominations.includes(denomination.denominationID),
        );
        setDenominations(filteredDenominations);
      } catch (error) {
        setErrorMessage("Failed to load denominations.");
        console.error("Error fetching denominations:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };

    fetchDenominations();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof Bishop,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setBishop({
      ...bishop,
      [field]: value,
    });
  };

  const handleCreateBishop = async () => {
    if (!bishop.diocese || !bishop.bishopName) {
      setErrorMessage("Name and Diocese are required fields.");
      return;
    }

    setLoadingSpinner(true);
    try {
      await createBishop(bishop);
      setSuccessMessage("Bishop created successfully.");
      setTimeout(() => navigate("/admin/master-lists/bishops"), 1000);
    } catch (error) {
      console.error("Error creating bishop:", error);
      setErrorMessage("Failed to create bishop. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          <h1>Create New Bishop</h1>
        </div>
      </div>

      <div className="divider"></div>
      <div className="data-container">
        <div className="field-stacked" style={{ flex: "0 0 50%" }}>
          <label className="field-label">Merge Field/Organization Name:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.diocese}
              onChange={(e) => handleInputChange(e, "diocese")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Denomination:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={bishop?.denomination || ""}
              onChange={(e) => handleInputChange(e, "denomination")}
            >
              <option value="">Select a Denomination</option>
              {denominations.map((denomination) => (
                <option
                  key={denomination.denominationID}
                  value={denomination.denominationName}
                >
                  {denomination.denominationName}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 50%" }}>
          <label className="field-label">Client Abbreviation:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop?.bishopName || ""}
              onChange={(e) => handleInputChange(e, "bishopName")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 10%" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 40%" }}>
          <label className="field-label">Contact:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop?.contact || ""}
              onChange={(e) => handleInputChange(e, "contact")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Greeting:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.greeting}
              onChange={(e) => handleInputChange(e, "greeting")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 10%" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">Address:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.address}
              onChange={(e) => handleInputChange(e, "address")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">Address 2:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop?.address2 || ""}
              onChange={(e) => handleInputChange(e, "address2")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">City:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.city}
              onChange={(e) => handleInputChange(e, "city")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">State:</label>
          <select
            className="form-select"
            value={bishop.state}
            onChange={(e) => handleInputChange(e, "state")}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.abbreviation} value={state.abbreviation}>
                {state.name} ({state.abbreviation})
              </option>
            ))}
          </select>
        </div>
        {bishop?.state === "INT" && (
          <div className="field-stacked" style={{ flex: "0 0 15%" }}>
            <label className="field-label">Country:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={bishop?.country || ""}
                onChange={(e) => handleInputChange(e, "country")}
              />
            </div>
          </div>
        )}
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">ZipCode:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.zipCode}
              onChange={(e) => handleInputChange(e, "zipCode")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 40%" }}>
          <label className="field-label">Email:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.email}
              onChange={(e) => handleInputChange(e, "email")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">Phone:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={bishop.phone}
              onChange={(e) => handleInputChange(e, "phone")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Send Certified:</label>
            <ToolTip
              tooltipText="Controls if csv box is visible on the reference tracking page once used to build a reference."
              placement="left"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div>
            <input
              className="form-check-input"
              type="checkbox"
              checked={bishop?.sendCertified ? bishop?.sendCertified : false}
              onChange={(e) => handleInputChange(e, "sendCertified")}
            />
          </div>
        </div>
        {/* <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Verified:</label>
          </div>
          <div>
            <input
              className={`form-check-input`}
              type="checkbox"
              checked={bishop?.verified}
              onChange={(e) => handleInputChange(e, "verified")}
            />
          </div>
        </div> */}
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Comments:</label>
          <div className="field-input">
            <textarea
              className="form-control"
              value={bishop.comments}
              onChange={(e) => handleInputChange(e, "comments")}
              style={{ height: "100px" }}
              maxLength={4000}
            />
            <div
              style={{
                fontSize: "14px",
                marginTop: "4px",
                textAlign: "right",
              }}
            >
              {bishop.comments?.length ?? 0}/4000
            </div>
          </div>
        </div>

        <div className="btn-center" style={{ flex: "0 0 100%" }}>
          <button className="oxbtn btn-green" onClick={handleCreateBishop}>
            Create
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {errorMessage && (
        <div className="alert-message alert alert-danger">
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
          />
        </div>
      )}

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CreateBishop;
